import React, { useEffect, useRef, useState } from "react";
import "../css/NewsSlider.css";
import { GrPrevious } from "react-icons/gr";
import { GrNext } from "react-icons/gr";
import { useNavigate } from "react-router-dom";

export default function NewsSlider({
  items = [],
  title = "Bài viết liên quan",
}) {
  const containerRef = useRef(null);
  const trackRef = useRef(null);

  const [canPrev, setCanPrev] = useState(false);
  const [canNext, setCanNext] = useState(false);
  const [stepPx, setStepPx] = useState(0);

  const navigate = useNavigate();

  // Determine items per page based on Bootstrap breakpoints (md=768, lg=992)
  const getItemsPerPage = () => {
    if (window.matchMedia("(min-width: 992px)").matches) return 3; // desktop
    if (window.matchMedia("(min-width: 768px)").matches) return 2; // tablet
    return 1; // mobile
  };

  const updateButtons = () => {
    const scroller = containerRef.current;
    if (!scroller) return;
    const maxScrollLeft = scroller.scrollWidth - scroller.clientWidth;
    setCanPrev(scroller.scrollLeft > 0);
    setCanNext(scroller.scrollLeft < maxScrollLeft - 1);
  };

  const computeStep = () => {
    const scroller = containerRef.current;
    const track = trackRef.current;
    if (!scroller || !track) return;

    const firstItem = track.querySelector(".slider-item");
    if (!firstItem) {
      setStepPx(0);
      return;
    }

    const itemWidth = firstItem.getBoundingClientRect().width;
    // Read the gap from CSS (defaults to 16px)
    const styles = getComputedStyle(track);
    const gap = parseFloat(styles.columnGap || styles.gap || "16") || 16;

    setStepPx(itemWidth + gap);
    updateButtons();
  };

  const scrollByOne = (direction = 1) => {
    const scroller = containerRef.current;
    if (!scroller || stepPx <= 0) return;
    scroller.scrollBy({ left: direction * stepPx, behavior: "smooth" });
  };

  useEffect(() => {
    computeStep();
    const onResize = () => {
      // Recalculate step on resize (breakpoints change item width)
      computeStep();
    };
    const onScroll = () => updateButtons();

    window.addEventListener("resize", onResize);
    const scroller = containerRef.current;
    scroller?.addEventListener("scroll", onScroll, { passive: true });

    // Initialize button states
    updateButtons();

    return () => {
      window.removeEventListener("resize", onResize);
      scroller?.removeEventListener("scroll", onScroll);
    };
    // Recompute when items change
  }, [items.length]);

  const handleOnClickSliderItem = (id) => {
    navigate(`/news/${id}`);
  }
  const itemsPerPage = getItemsPerPage();
  const isShort = items.length <= itemsPerPage;

  return (
    <section className="news-slider">
      <div className="d-flex align-items-center justify-content-between my-4">
        <h5 className="mb-0 news-slider-title">{title}</h5>
        <div
          className="btn-group"
          role="group"
          aria-label="Slider controls"
        ></div>
      </div>
      <button
        type="button"
        className="btn btn-outline-primary btn-prev"
        onClick={() => scrollByOne(-1)}
        disabled={!canPrev || isShort}
        aria-label="Previous"
      >
        <GrPrevious />
      </button>
      <div
        ref={containerRef}
        className="slider-scroller"
        tabIndex={0}
        aria-label={`${title} slider`}
      >
        <div ref={trackRef} className="slider-track">
          {items.map((item, idx) => (
            <div key={idx} className="slider-item news-card" onClick={() => handleOnClickSliderItem(item.id)}>
              <div className="card h-100">
                {item.image && (
                  <img
                    src={item.image}
                    className="card-img-top"
                    alt={item.title || `Card ${idx + 1}`}
                  />
                )}
                <div className="card-body">
                  <small className="text-muted news-date">{item.date}</small>
                  {item.title && <h6 className="card-title">{item.title}</h6>}
                  {item.text && <p className="card-text mb-2">{item.text}</p>}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <button
        type="button"
        className="btn btn-outline-primary btn-next"
        onClick={() => scrollByOne(1)}
        disabled={!canNext || isShort}
        aria-label="Next"
      >
        <GrNext />
      </button>
    </section>
  );
}
